import { createSlice } from '@reduxjs/toolkit';
import { loadSetting, saveSetting } from '@utils/localStorage';
import { getCurrentTz, getDetectedTz, setCurrentTz, TZ_FALLBACK } from '@utils/tzRegistry';

import type { PayloadAction } from '@reduxjs/toolkit';

const STORAGE_KEY = 'Timezone';

const isValidIanaTz = (tz: unknown): tz is string => {
    if (typeof tz !== 'string' || !tz) return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};

const resolveInitialTz = (): string => {
    const stored = loadSetting(STORAGE_KEY);
    if (isValidIanaTz(stored)) return stored;
    const detected = getDetectedTz();
    return isValidIanaTz(detected) ? detected : TZ_FALLBACK;
};

export interface TimezoneState {
    tz: string;
}

const initialTz = resolveInitialTz();
setCurrentTz(initialTz);

const initialState: TimezoneState = { tz: initialTz };

export const timezoneSlice = createSlice({
    name: 'timezone',
    initialState,
    reducers: {
        setTimezone: (state, action: PayloadAction<string>) => {
            const next = isValidIanaTz(action.payload) ? action.payload : TZ_FALLBACK;
            saveSetting(STORAGE_KEY, next);
            setCurrentTz(next);
            state.tz = next;
        },
        resetTimezone: (state) => {
            const next = isValidIanaTz(getDetectedTz()) ? getDetectedTz() : TZ_FALLBACK;
            saveSetting(STORAGE_KEY, next);
            setCurrentTz(next);
            state.tz = next;
        },
    },
});

export const { setTimezone, resetTimezone } = timezoneSlice.actions;
export default timezoneSlice.reducer;

// Convenience getter for non-component code that doesn't need the slice
export { getCurrentTz };
